<template>
    <div class="computerBox">
        <topBox :title="$route.meta.title" />
        <div class="px-[36px] w-full mt-[80px] pb-[70px]">

            <!-- 当前有效期 -->
            <div class="my-2">
                <div class="flex flex-col items-center rounded-[24px] border bg-white px-[35px] py-[20px] shadow-lg">
                    <div class="text-xs text-slate-400">{{ $t('text.currentValidUntil') }}</div>
                    <div class="mt-2 text-m14 font-semibold" :class="isActive ? 'text-green-600' : 'text-slate-900'">
                        {{ validUntilText }}
                    </div>
                    <div v-if="effectiveAt > 0" class="mt-1 text-xs" :class="isActive ? 'text-green-500' : 'text-slate-400'">
                        {{ isActive ? $t('text.serviceActive') : $t('text.serviceExpired') }}
                    </div>
                </div>
            </div>

            <!-- 服务套餐 -->
            <div class="my-3">
                <h1 class="text-m14 font-semibold text-slate-900 mb-[10px]">{{ $t('text.servicePlan') }}</h1>

                <div
                    v-for="plan in plans"
                    :key="plan.id"
                    class="plan-card mb-3"
                    :class="{ 'plan-card--selected': selected === plan.id }"
                    @click="selected = plan.id"
                >
                    <div class="flex items-center justify-between">
                        <div class="text-left">
                            <div class="text-m14 font-semibold text-slate-900">{{ planTitle(plan.id) }}</div>
                            <div class="mt-1 fz12 text-slate-400">{{ planDays(plan) }}</div>
                        </div>
                        <div class="text-right">
                            <div class="text-m14 font-semibold text-green-600">¥{{ plan.price }}</div>
                            <div class="mt-1 fz12 text-slate-400">{{ $t('text.rmb') }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 支付方式 -->
            <div class="my-3">
                <h1 class="text-m14 font-semibold text-slate-900 mb-[10px]">{{ $t('text.payMethod') }}</h1>
                <div class="flex gap-3">
                    <button
                        v-for="item in payTypes"
                        :key="item.id"
                        type="button"
                        class="pay-type-btn flex-1"
                        :class="{ 'pay-type-btn--selected': payType === item.id }"
                        @click="payType = item.id"
                    >
                        {{ $t('text.' + item.name) }}
                    </button>
                </div>
            </div>

            <!-- 购买 -->
            <div class="py-[10px]">
                <button
                    class="block w-full px-3 py-[15px] border rounded-full butSubmit text-white shadow-lg focus:ring-1 focus:outline-none text-xs-xs"
                    :disabled="buying || !selected"
                    @click="buy"
                >
                    {{ buying ? $t('text.buying') : $t('but.buyNow') }}
                </button>
            </div>

            <div class="pb-[10px]">
                <router-link
                    to="/computer/recharge/list"
                    class="block w-full px-3 py-[15px] border rounded-full bg-white text-slate-700 shadow-lg text-center text-xs-xs no-underline"
                >
                    {{ $t('text.rechargeRecord') }}
                </router-link>
            </div>

            <p class="mt-2 fz12 text-slate-400 leading-relaxed text-center">{{ $t('text.subscribeTip') }}</p>
            <p v-if="pendingPay" class="mt-2 fz12 text-amber-600 leading-relaxed text-center">{{ $t('text.payPendingTip') }}</p>

        </div>

        <!-- 支付二维码：按 API 文档用 qrcode 链接生成二维码，等待用户扫码支付 -->
        <div v-if="qrVisible" class="qr-mask" @click.self="closeQr">
            <div class="qr-panel">
                <div class="text-m14 font-semibold text-slate-900">{{ payQrTitle }}</div>
                <p class="mt-2 text-xs text-slate-400 leading-relaxed">{{ payQrTip }}</p>
                <div class="mt-4 flex justify-center">
                    <canvas ref="payQrCanvas" class="h-[200px] w-[200px] rounded-lg bg-white"></canvas>
                </div>
                <p v-if="payWaiting" class="mt-3 text-xs text-amber-600 text-center">{{ $t('text.payWaiting') }}</p>
                <button
                    type="button"
                    class="mt-4 block w-full px-3 py-[12px] border rounded-full bg-slate-100 text-slate-700 text-xs-xs"
                    @click="closeQr"
                >
                    {{ $t('but.close') }}
                </button>
            </div>
        </div>

        <mainMenu />
    </div>
</template>

<script>
    import { useI18n } from 'vue-i18n'
    import QRCode from 'qrcode'
    import topBox from '@/components/computer/TopBox.vue'
    import mainMenu from '@/components/computer/MainMenu.vue'
    import axios from 'axios'
    import { layer } from 'vue3-layer'

    export default {
        components: {
            topBox,
            mainMenu,
        },
        data() {
            return {
                lang: '',
                effectiveAt: 0,
                isActive: false,
                selected: 'month',
                payType: 'alipay',
                payTypes: [
                    { id: 'alipay', name: 'payAlipay' },
                    { id: 'wxpay', name: 'payWxpay' },
                ],
                buying: false,
                pendingPay: false,
                qrVisible: false,
                qrcodeUrl: '',
                payWaiting: false,
                baselineEffectiveAt: 0,
                pollTimer: null,
                plans: [
                    { id: 'month', months: 1, days: 31, price: 50 },
                    { id: 'quarter', months: 3, days: 93, price: 130 },
                    { id: 'half_year', months: 6, days: 186, price: 240 },
                    { id: 'year', months: 12, days: 365, price: 360 },
                ],
            }
        },
        computed: {
            validUntilText() {
                if (!this.effectiveAt) {
                    return this.$t('text.notActivated')
                }
                return this.formatTime(this.effectiveAt)
            },
            payQrTitle() {
                return this.payType === 'wxpay' ? this.$t('text.payWxpay') : this.$t('text.payAlipay')
            },
            payQrTip() {
                return this.payType === 'wxpay' ? this.$t('text.payScanWxTip') : this.$t('text.payScanAlipayTip')
            },
        },
        mounted() {
            const { locale } = useI18n()
            this.lang = localStorage.getItem('lang') ? localStorage.getItem('lang') : locale.value
            locale.value = this.lang
            this.pendingPay = this.$route.query.from === 'pay'
            const planQuery = String(this.$route.query.plan || '')
            if (['month', 'quarter', 'half_year', 'year'].includes(planQuery)) {
                this.selected = planQuery
            }
            this.loadInfo()
        },
        beforeUnmount() {
            this.stopPoll()
        },
        methods: {
            headers() {
                return {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept-Language': this.lang,
                    token: localStorage.getItem('token'),
                }
            },
            planTitle(id) {
                const key = {
                    month: 'monthlyPlan',
                    quarter: 'quarterlyPlan',
                    half_year: 'halfYearPlan',
                    year: 'yearPlan',
                }[id]
                return key ? this.$t('text.' + key) : id
            },
            planDays(plan) {
                const key = {
                    month: 'monthlyDays',
                    quarter: 'quarterlyDays',
                    half_year: 'halfYearDays',
                    year: 'yearDays',
                }[plan.id]
                return key ? this.$t('text.' + key) : this.$t('text.planDaysFallback', { days: plan.days })
            },
            formatTime(ts) {
                const d = new Date(ts * 1000)
                const pad = (n) => (n < 10 ? '0' + n : '' + n)
                return (
                    d.getFullYear() +
                    '-' +
                    pad(d.getMonth() + 1) +
                    '-' +
                    pad(d.getDate()) +
                    ' ' +
                    pad(d.getHours()) +
                    ':' +
                    pad(d.getMinutes())
                )
            },
            loadInfo() {
                return axios
                    .get('/api/m/subscribe/info', { headers: this.headers() })
                    .then((response) => {
                        if (response.data.code === 0) {
                            const data = response.data.data
                            this.effectiveAt = data.effective_at || 0
                            this.isActive = !!data.is_active
                            if (Array.isArray(data.plans) && data.plans.length) {
                                this.plans = data.plans
                            }
                            return data
                        }
                        if (response.data.code === 911) localStorage.removeItem('isLogin')
                        layer.msg(response.data.msg)
                        return null
                    })
                    .catch((error) => {
                        console.log(error)
                        return null
                    })
            },
            buy() {
                if (this.buying || !this.selected) return
                this.buying = true
                this.baselineEffectiveAt = this.effectiveAt
                axios
                    .post(
                        '/api/m/subscribe/buy',
                        { plan: this.selected, type: this.payType },
                        { headers: this.headers() }
                    )
                    .then((response) => {
                        if (response.data.code === 0) {
                            const data = response.data.data || {}
                            // 文档：payurl / qrcode / urlscheme 三者只返回其一
                            if (data.qrcode) {
                                this.showPayQr(data.qrcode)
                                return
                            }
                            if (data.payurl) {
                                layer.msg(this.$t('text.payRedirecting'))
                                window.location.href = data.payurl
                                return
                            }
                            if (data.urlscheme) {
                                window.location.href = data.urlscheme
                                return
                            }
                            layer.msg(this.$t('text.payCreateFailed'))
                        } else {
                            if (response.data.code === 911) localStorage.removeItem('isLogin')
                            layer.msg(response.data.msg)
                        }
                    })
                    .catch((error) => {
                        console.log(error)
                        layer.msg(this.$t('text.payCreateFailed'))
                    })
                    .finally(() => {
                        this.buying = false
                    })
            },
            showPayQr(url) {
                this.qrcodeUrl = url
                this.qrVisible = true
                this.payWaiting = true
                this.pendingPay = true
                this.$nextTick(() => this.renderPayQr())
                this.startPoll()
            },
            renderPayQr() {
                const canvas = this.$refs.payQrCanvas
                if (!canvas || !this.qrcodeUrl) return
                QRCode.toCanvas(canvas, this.qrcodeUrl, {
                    width: 200,
                    margin: 2,
                    color: { dark: '#0f172a', light: '#ffffff' },
                }).catch((error) => {
                    console.log(error)
                    layer.msg(this.$t('text.payCreateFailed'))
                })
            },
            startPoll() {
                this.stopPoll()
                this.pollTimer = setInterval(() => {
                    this.loadInfo().then((data) => {
                        if (!data) return
                        const next = data.effective_at || 0
                        if (next > this.baselineEffectiveAt) {
                            this.onPaySuccess()
                        }
                    })
                }, 3000)
            },
            stopPoll() {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer)
                    this.pollTimer = null
                }
            },
            onPaySuccess() {
                this.stopPoll()
                this.qrVisible = false
                this.qrcodeUrl = ''
                this.payWaiting = false
                this.pendingPay = false
                layer.msg(this.$t('text.buySuccess'))
            },
            closeQr() {
                this.stopPoll()
                this.qrVisible = false
                this.qrcodeUrl = ''
                this.payWaiting = false
            },
        },
    }
</script>

<style lang="scss" scoped>
.computerBox {
    /* background: url('https://tdkt06.com/img/headbg2x.1c5aca6e.png') 0% 0% / 100% no-repeat; */
}

.plan-card {
    border-radius: 20px;
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 20px 24px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.plan-card--selected {
    border-color: #22c55e;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.pay-type-btn {
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    background: #fff;
    padding: 12px 16px;
    font-size: 14px;
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pay-type-btn--selected {
    border-color: #22c55e;
    color: #16a34a;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}

button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.qr-mask {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.45);
    padding: 24px;
}

.qr-panel {
    width: 100%;
    max-width: 320px;
    border-radius: 24px;
    background: #fff;
    padding: 28px 24px;
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
    text-align: center;
}
</style>
